import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { sortByTitle } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import { StatusBadge } from "@/components/StatusBadge";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { StackIcon, FolderIcon, CheckSquareIcon, UsersThreeIcon, ArrowUpRightIcon } from "@phosphor-icons/react";
import { STATUS_STYLES } from "@/components/StatusBadge";

const STAT_ICONS = {
  initiatives: StackIcon, projects: FolderIcon, tasks: CheckSquareIcon, users: UsersThreeIcon,
};

const PIE_COLORS = {
  open: "#94A3B8",
  wip: "#0176D3",
  test: "#7C3AED",
  uat: "#0891B2",
  cutover: "#EA580C",
  live: "#059669",
  closed: "#047857",
  cancelled: "#B45309",
  hold: "#D97706",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, i, p] = await Promise.all([
        api.get("/stats/overview"),
        api.get("/initiatives"),
        api.get("/projects"),
      ]);
      setStats(s.data);
      setInitiatives(sortByTitle(i.data));
      setProjects(sortByTitle(p.data));
    })();
  }, []);

  if (!stats) {
    return (
      <AppLayout>
        <div className="p-10 text-sm text-zinc-500">Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Overview</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Dashboard</h1>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          LIVE — {new Date().toLocaleDateString()}
        </div>
      </div>

      <div className="px-8 py-6 space-y-8 trk-fade-in">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 border border-border rounded-sm overflow-hidden">
          {Object.entries(stats.counts).map(([k, v], i) => {
            const Icon = STAT_ICONS[k];
            return (
              <div key={k} className={`p-5 bg-white ${i < 3 ? "border-r border-border" : ""}`} data-testid={`stat-${k}`}>
                <div className="flex items-center justify-between">
                  <div className="trk-uppercase-label">{k}</div>
                  {Icon && <Icon size={16} className="text-zinc-400" />}
                </div>
                <div className="font-display font-extrabold text-4xl mt-3 tabular-nums">{v}</div>
              </div>
            );
          })}
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-border rounded-sm p-5" data-testid="chart-task-completion">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="trk-uppercase-label">Task Completion</div>
                <h3 className="font-display text-lg font-bold">Last 30 days</h3>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer debounce={250}>
                <LineChart data={stats.completion_series}>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A" }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#71717A" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                  <Line type="monotone" dataKey="completed" stroke="#0176D3" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-border rounded-sm p-5" data-testid="chart-status-pie">
            <div className="trk-uppercase-label">Distribution</div>
            <h3 className="font-display text-lg font-bold mb-4">Task Status</h3>
            <div className="h-48">
              <ResponsiveContainer debounce={250}>
                <PieChart>
                  <Pie data={stats.status_distribution} dataKey="count" nameKey="status" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {stats.status_distribution.map((e) => (
                      <Cell key={e.status} fill={PIE_COLORS[e.status]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5 mt-2">
              {stats.status_distribution.map((s) => (
                <div key={s.status} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[s.status] }} />
                    <span className="text-zinc-600">{STATUS_STYLES[s.status]?.label}</span>
                  </div>
                  <span className="font-mono">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Workload */}
        <div className="border border-border rounded-sm p-5" data-testid="chart-workload">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="trk-uppercase-label">Workload</div>
              <h3 className="font-display text-lg font-bold">Tasks by team member</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer debounce={250}>
              <BarChart data={stats.workload.slice(0, 10)}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#71717A" }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: 4, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="open" stackId="a" fill="#94A3B8" />
                <Bar dataKey="wip" stackId="a" fill="#0176D3" />
                <Bar dataKey="test" stackId="a" fill="#7C3AED" />
                <Bar dataKey="uat" stackId="a" fill="#0891B2" />
                <Bar dataKey="cutover" stackId="a" fill="#EA580C" />
                <Bar dataKey="live" stackId="a" fill="#059669" />
                <Bar dataKey="closed" stackId="a" fill="#047857" />
                <Bar dataKey="cancelled" stackId="a" fill="#B45309" />
                <Bar dataKey="hold" stackId="a" fill="#D97706" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent lists */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="border border-border rounded-sm" data-testid="recent-initiatives">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="trk-uppercase-label">Recent</div>
                <h3 className="font-display font-bold">Initiatives</h3>
              </div>
              <Link to="/initiatives" className="text-xs font-mono uppercase tracking-wider text-[#0176D3] hover:underline flex items-center gap-1">
                View all <ArrowUpRightIcon size={12} />
              </Link>
            </div>
            <div>
              {initiatives.slice(0, 5).map((i) => (
                <Link key={i._id} to={`/initiatives/${i._id}`} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0 hover:bg-zinc-50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{i.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{i.description}</div>
                  </div>
                  <StatusBadge status={i.status} />
                </Link>
              ))}
              {initiatives.length === 0 && (
                <div className="p-6 text-sm text-zinc-500">No initiatives yet.</div>
              )}
            </div>
          </div>

          <div className="border border-border rounded-sm" data-testid="recent-projects">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="trk-uppercase-label">Recent</div>
                <h3 className="font-display font-bold">Projects</h3>
              </div>
              <Link to="/projects" className="text-xs font-mono uppercase tracking-wider text-[#0176D3] hover:underline flex items-center gap-1">
                View all <ArrowUpRightIcon size={12} />
              </Link>
            </div>
            <div>
              {projects.slice(0, 5).map((p) => (
                <Link key={p._id} to={`/projects/${p._id}`} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0 hover:bg-zinc-50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.description}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </Link>
              ))}
              {projects.length === 0 && (
                <div className="p-6 text-sm text-zinc-500">No projects yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
